import { AppValidator } from "@/common/app-validate.ts";
import CustomRequiredMarkAntdForm from "@/component/CustomRequiredMarkAntdForm.tsx";
import ManageContentLayout from "@/component/common/layout/ManageContentLayout.tsx";
import LicenseRegistrationStatusTag from "@/component/tag/LicenseRegistrationStatusTag.tsx";
import {
    LICENSE_REGISTRATION_ENUM,
    REGEX,
    STATUS_PAGE_MAP,
} from "@/constant/constants.ts";
import { MANAGER_PATH } from "@/constant/url-path.ts";
import useProductOptions from "@/hooks/useProductOptions.tsx";
import { LicenseRegistrationService } from "@/services/licenseRegistration/LicenseRegistrationService.ts";
import { toast } from "@/utils/toast.tsx";
import LicenseManagement from "@/view/license/LicenseManagement.tsx";
import ExtendButton from "@/view/license/component/ExtendLicenseButton.tsx";
import LicenseRegistrationInfo from "@/view/license/component/LicenseRegistrationInfo.tsx";
import {
    Button,
    Checkbox,
    Col,
    Divider,
    Flex,
    Form,
    Input,
    InputNumber,
    Row,
    Select,
    Space,
} from "antd";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";

const AddLicenseRegistrationPage = ({
  statusPage = STATUS_PAGE_MAP.DETAIL,
}: {
  statusPage?: number;
}) => {
  const { t } = useTranslation();
  const navigation = useNavigate();
  const location = useLocation();
  const validators = AppValidator(t);
  const productOptions = useProductOptions();

  const [licenseRegistrationInfoForm] =
    Form.useForm<CreateLicenseRegistrationRequest>();
  const queryParams = new URLSearchParams(location.search);
  const licenseRegistrationId = queryParams.get("id") || null;

  const [detailLicense, setDetailLicense] =
    useState<LicenseRegistrationDetail>();

  const optionNames = [
    "EDEV",
    "ETEST",
    "Cobol To Java",
    "Delphi To C#",
    "JSP To Thymeleaf",
    "o3w xml To Spring Web",
    "ASP To JSP",
    "VB.net To Java",
    "VBScript To Java",
    "TODO",
    "TODO",
    "TODO",
    "TODO",
    "TODO",
    "TODO",
    "TODO",
    "TODO",
    "TODO",
  ];

  const getLicenseRegistration = useCallback(async () => {
    if (licenseRegistrationId) {
      const res: LicenseRegistrationDetail | null | undefined =
        await LicenseRegistrationService.getDetail(licenseRegistrationId);
      if (res) {
        const requestValues = res as CreateLicenseRegistrationRequest;
        licenseRegistrationInfoForm.setFieldsValue({
          ...requestValues,
          checkboxes: requestValues.checkboxes ?? undefined,
        });
        setDetailLicense(res);
      }
    }
  }, [licenseRegistrationId, licenseRegistrationInfoForm]);

  useEffect(() => {
    if (statusPage !== STATUS_PAGE_MAP.CREATE) {
      getLicenseRegistration().then();
    }
  }, [getLicenseRegistration, statusPage]);

  const onSubmit = (values: any) => {
    if (statusPage === STATUS_PAGE_MAP.EDIT) {
      onEdit(values).then();
    } else if (statusPage === STATUS_PAGE_MAP.CREATE) {
      onSave(values).then();
    }
  };

  const onSave = async (values: CreateLicenseRegistrationRequest) => {
    const { licenseOptions, ...rest } =
      values as CreateLicenseRegistrationRequest & {
        licenseOptions?: number[] | null;
      };
    const selectedOptions = licenseOptions ?? [];
    const checkboxes = optionNames.map((_, index) =>
      selectedOptions.includes(index),
    );
    const res: SuccessResponse | null | undefined =
      await LicenseRegistrationService.create({
        ...rest,
        checkboxes,
      });
    if (res) {
      navigation(MANAGER_PATH.LICENSE_REGISTRATION.LIST);
      toast.success(res?.message || "Success");
    }
  };

  const onEdit = async (values: CreateLicenseRegistrationRequest) => {
    if (licenseRegistrationId) {
      const res: SuccessResponse | null | undefined =
        await LicenseRegistrationService.update(licenseRegistrationId, values);
      if (res) {
        const params = new URLSearchParams();
        params.append("id", String(licenseRegistrationId));
        navigation(
          `${MANAGER_PATH.LICENSE_REGISTRATION.DETAIL}?${params.toString()}`,
        );
        toast.success(res?.message || "Success");
      }
    }
  };

  const onCancel = () => {
    if (
      statusPage === STATUS_PAGE_MAP.CREATE ||
      statusPage === STATUS_PAGE_MAP.DETAIL
    ) {
      navigation(MANAGER_PATH.LICENSE_REGISTRATION.LIST);
    }
    if (statusPage === STATUS_PAGE_MAP.EDIT) {
      const params = new URLSearchParams();
      params.append("id", String(licenseRegistrationId));
      navigation(
        `${MANAGER_PATH.LICENSE_REGISTRATION.DETAIL}?${params.toString()}`,
      );
    }
  };

  const renderProductOption = (option: any) => (
    <Space>
      <span role="img">
        {option.data.productName} - {`(${option.data.version})`}
      </span>
    </Space>
  );

  const formatProductLabel = (option: ProductGroupByNameOption) => {
    const versions = option.version?.filter(Boolean) ?? [];
    if (versions.length === 0) {
      return option.productName ?? "";
    }
    return `${option.productName ?? ""} (${versions.join(", ")})`;
  };

  const productSelectOptions = productOptions.map((option) => ({
    ...option,
    displayLabel: formatProductLabel(option),
  }));

  const filterProductOption = (
    input: string,
    option?: ProductGroupByNameOption,
  ) => (option?.productName ?? "").toLowerCase().includes(input.toLowerCase());

  const changeNumberOfLicenseByNumberOfMac = async () => {
    await licenseRegistrationInfoForm
      .validateFields(["addressMac"])
      .then((value) => {
        const macsString = value.addressMac
          ? value.addressMac.trim().replace(/^;|;$/g, "")
          : null;
        const macList = macsString ? macsString.split(";") : [];
        licenseRegistrationInfoForm.setFieldValue(
          "numberOfLicense",
          macList.length,
        );
      })
      .catch(() => {});
  };

  return (
    <>
      <ManageContentLayout
        headerFunction={
          <>
            {detailLicense?.status ===
              LICENSE_REGISTRATION_ENUM.OVERDUE_RELEASE && (
              <ExtendButton
                buttonText={t("button.recreate")}
                detailRegistration={detailLicense}
              />
            )}
          </>
        }
      >
        {/* License Registration form block */}
        <div className={"relative p-5 mb-5 rounded-lg border bg-white"}>
          <div className={"absolute right-5 top-2"}>
            {detailLicense?.status ? (
              <LicenseRegistrationStatusTag
                status={detailLicense.status}
                hasIcon
              />
            ) : (
              "-"
            )}
          </div>
          {statusPage === STATUS_PAGE_MAP.DETAIL && (
            <div className={"mb-5"}>
              <LicenseRegistrationInfo info={detailLicense} />
            </div>
          )}
          <Form
            form={licenseRegistrationInfoForm}
            name="license-registration-info-form"
            layout={"vertical"}
            colon={false}
            requiredMark={CustomRequiredMarkAntdForm}
            onFinish={onSubmit}
            initialValues={{
              duration: 1,
              numberOfLicense: 1,
            }}
            style={{
              display: statusPage !== STATUS_PAGE_MAP.DETAIL ? "block" : "none",
            }}
            disabled={statusPage === STATUS_PAGE_MAP.DETAIL}
            size={"large"}
          >
            <div className={"mb-5"}>
              <div className={"text-base font-semibold"}>
                {t("field.companyName")}
              </div>
              <div className={"text-xs text-gray-500"}>
                {t("placeholder.enterRegistrantCompanyName")}
              </div>
            </div>

            <Row gutter={[16, 0]}>
              <Col xs={24} sm={12}>
                <Form.Item<CreateLicenseRegistrationRequest>
                  name="firstName"
                  label={t("field.firstName")}
                  rules={[validators.required(t("field.firstName"))]}
                >
                  <Input
                    maxLength={255}
                    allowClear
                    placeholder={t("placeholder.enterRegistrantFirstName")}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12}>
                <Form.Item<CreateLicenseRegistrationRequest>
                  name="lastName"
                  label={t("field.lastName")}
                  rules={[validators.required(t("field.lastName"))]}
                >
                  <Input
                    maxLength={255}
                    allowClear
                    placeholder={t("placeholder.enterRegistrantLastName")}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12}>
                <Form.Item<CreateLicenseRegistrationRequest>
                  name="registrantEmail"
                  label={t("field.email")}
                  rules={[
                    validators.required(t("field.email")),
                    validators.personalEmail(),
                  ]}
                >
                  <Input
                    maxLength={320}
                    allowClear
                    placeholder={t("placeholder.enterRegistrantEmail")}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12}>
                <Form.Item<CreateLicenseRegistrationRequest>
                  name="registrantMobile"
                  label={t("field.mobile")}
                  rules={[
                    validators.required(t("field.mobile")),
                    validators.minLength(9, t("field.mobile")),
                    validators.validateFormatWithRegex(
                      REGEX.NUMERIC,
                      t("field.mobile"),
                    ),
                  ]}
                >
                  <Input
                    maxLength={15}
                    allowClear
                    placeholder={t("placeholder.enterRegistrantMobile")}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12}>
                <Form.Item<CreateLicenseRegistrationRequest>
                  name="companyName"
                  label={t("field.companyName")}
                  rules={[validators.required(t("field.companyName"))]}
                >
                  <Input
                    maxLength={255}
                    allowClear
                    placeholder={t("placeholder.enterRegistrantCompanyName")}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12}>
                <Form.Item<CreateLicenseRegistrationRequest>
                  name="departmentName"
                  label={t("field.departmentName")}
                >
                  <Input
                    maxLength={255}
                    allowClear
                    placeholder={t("placeholder.enterRegistrantDepartmentName")}
                  />
                </Form.Item>
              </Col>
            </Row>

            <Divider className={"my-2"} />

            <div className={"mb-5"}>
              <div className={"text-base font-semibold"}>
                {t("field.productName")}
              </div>
            </div>

            <Row gutter={[16, 0]}>
              <Col xs={24} sm={12} lg={8}>
                <Form.Item<CreateLicenseRegistrationRequest>
                  name="productCode"
                  label={t("field.productName")}
                  rules={[validators.required(t("field.productName"))]}
                >
                  <Select
                    options={productSelectOptions}
                    placeholder={t("placeholder.selectProductName")}
                    fieldNames={{
                      label: "displayLabel",
                      value: "productCode",
                    }}
                    optionRender={renderProductOption}
                    showSearch
                    filterOption={filterProductOption}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12} lg={8}>
                <Form.Item<CreateLicenseRegistrationRequest>
                  name="duration"
                  label={t("field.duration")}
                  rules={[validators.required(t("field.duration"))]}
                >
                  <InputNumber
                    min={0}
                    className={"w-full"}
                    suffix={t("field.months")}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12} lg={8}>
                <Form.Item<CreateLicenseRegistrationRequest>
                  name="numberOfLicense"
                  label={t("field.numberOfLicense")}
                >
                  <InputNumber className={"w-full"} min={0} max={1000} />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12} lg={8}>
                <Form.Item<CreateLicenseRegistrationRequest>
                  name="expirationDate"
                  label="Valid Date"
                >
                  <Input type="date" />
                </Form.Item>
              </Col>
            </Row>

            <Divider className={"my-2"} />

            <div className={"mb-5"}>
              <div className={"text-base font-semibold"}>
                {t("field.addressMac")}
              </div>
            </div>

            <Form.Item<CreateLicenseRegistrationRequest>
              name="addressMac"
              label={t("field.addressMac")}
              rules={[validators.validateAddressMacs()]}
            >
              <Input.TextArea
                rows={5}
                allowClear
                placeholder={t("placeholder.enterAddressMacList")}
                onChange={changeNumberOfLicenseByNumberOfMac}
              />
            </Form.Item>

            <Form.Item
              name="licenseOptions"
              label="Licensing to"
              className={"w-full"}
            >
              <Checkbox.Group
                className={
                  "w-full grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2"
                }
                options={optionNames.map((name, index) => ({
                  label: name,
                  value: index,
                }))}
              />
            </Form.Item>
            {(statusPage === STATUS_PAGE_MAP.EDIT ||
              statusPage === STATUS_PAGE_MAP.CREATE) && (
              <Form.Item className={"mb-1 mt-6"}>
                <div className={"flex justify-end gap-2 flex-wrap"}>
                  <Button
                    type="default"
                    className="m-0"
                    onClick={() => onCancel()}
                  >
                    {t("button.cancel")}
                  </Button>
                  <Button type="primary" htmlType="submit" className="m-0">
                    {statusPage === STATUS_PAGE_MAP.CREATE
                      ? t("button.add")
                      : t("button.save")}
                  </Button>
                </div>
              </Form.Item>
            )}
          </Form>
          {statusPage === STATUS_PAGE_MAP.DETAIL && (
            <div className={"text-end mt-3"}>
              <Button
                type="default"
                className="mx-1"
                size={"large"}
                onClick={() => onCancel()}
              >
                {t("button.cancel")}
              </Button>
              <Button
                type="primary"
                size={"large"}
                className="mx-1"
                onClick={() => {
                  const params = new URLSearchParams();
                  params.append(
                    "id",
                    String(
                      licenseRegistrationInfoForm.getFieldValue(
                        "licenseRegistrationId",
                      ),
                    ),
                  );
                  navigation(
                    `${MANAGER_PATH.LICENSE_REGISTRATION.EDIT}?${params.toString()}`,
                  );
                }}
              >
                {t("button.edit")}
              </Button>
            </div>
          )}
          {(detailLicense?.status === LICENSE_REGISTRATION_ENUM.ACTIVE ||
            detailLicense?.status ===
              LICENSE_REGISTRATION_ENUM.ALMOST_EXPIRED ||
            detailLicense?.status === LICENSE_REGISTRATION_ENUM.EXPIRED) && (
            <>
              <ExtendButton detailRegistration={detailLicense} />
            </>
          )}
          {detailLicense?.status ===
            LICENSE_REGISTRATION_ENUM.OVERDUE_RELEASE && (
            <Flex justify={"flex-end"}>
              <Button type="default" size={"large"} onClick={() => onCancel()}>
                {t("button.cancel")}
              </Button>
            </Flex>
          )}
        </div>
        <div>
          {(detailLicense?.status === LICENSE_REGISTRATION_ENUM.ACTIVE ||
            detailLicense?.status ===
              LICENSE_REGISTRATION_ENUM.ALMOST_EXPIRED ||
            detailLicense?.status === LICENSE_REGISTRATION_ENUM.EXPIRED) && (
            <>
              <LicenseManagement detailRegistration={detailLicense} />
            </>
          )}
        </div>
      </ManageContentLayout>
    </>
  );
};

export default AddLicenseRegistrationPage;
